/**
 * PDFExport — Génération de rapports PDF professionnels EkynAir
 *
 * Contenu : logo, infos session, stats PM, seuils référence,
 * recommandations, observations véto, graphiques (combined + PM + env + PM10pcs),
 * photos, carte
 *
 * Période analysée (10.5.314, D240) : `period` est l'objet de
 * buildPeriodAnalysis déjà calculé pour l'écran, jamais recalculé ici. Les
 * sections chiffrées (statistiques, résumé, AUC, recommandations, graphiques)
 * lisent sa session fenêtrée et ses mesures ; informations de session, notes
 * vétérinaires et carte restent la session. Sans période : PDF de la session.
 *
 * Bouton et état seulement (10.5.323, D247) : la génération vit dans
 * sessionPdfDocument.js. `sources` absent : sources du propriétaire (routes
 * authentifiées) ; la page du lien de séance passe les siennes.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { generateSessionPdf, ownerPdfSources } from './sessionPdfDocument';

const PDFExport = ({ session, sessionId, darkMode = false, readings = [], period = null, sources = null }) => {
  const { t, i18n } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState('');
  const [exportError, setExportError] = useState('');
  // Sections du PDF incomplètes (photos ignorées, liste non chargée, ex. 429) :
  // le PDF sort quand même, mais l'utilisateur le sait (10.5.300).
  const [exportWarnings, setExportWarnings] = useState([]);

  const generatePDF = async () => {
    setExporting(true);
    setExportError('');
    setExportWarnings([]);
    try {
      const warnings = await generateSessionPdf({
        session, sessionId, readings, period, t, language: i18n.language,
        onProgress: setProgress, sources: sources || ownerPdfSources(sessionId),
      });
      setExportWarnings(warnings);
    } catch (error) {
      // Échec global : aucun PDF enregistré, l'utilisateur doit le savoir.
      Sentry.captureException(error, { extra: { stage: 'pdf_generation' } });
      setExportError(t('pdf.overlay.error'));
    } finally {
      setExporting(false);
      setProgress('');
    }
  };

  return (
    <>
    {exporting && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          background: darkMode ? '#1E1E32' : '#fff', borderRadius: '12px', padding: '32px 40px',
          textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', minWidth: '280px',
        }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '50%', margin: '0 auto 16px',
            border: '3px solid #1B77F1', borderTopColor: 'transparent',
            animation: 'eky-spin 0.75s linear infinite',
          }} />
          <style>{`@keyframes eky-spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ fontSize: '15px', fontWeight: '600', color: darkMode ? '#E8EDF2' : '#2C3E50', marginBottom: '6px' }}>
            {t('pdf.overlay.title')}
          </div>
          <div style={{ fontSize: '13px', color: darkMode ? '#94A3B8' : '#6B7280' }}>
            {progress}
          </div>
        </div>
      </div>
    )}
    {/* Colonne : le message d'erreur reste sous le bouton dans la barre d'actions en flex. */}
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
    <button
      type="button"
      onClick={generatePDF}
      disabled={exporting}
      style={{
        padding: '12px 24px',
        backgroundColor: darkMode ? 'rgba(191,49,26,0.9)' : '#BF311A',
        color: '#fff', border: 'none', borderRadius: '8px',
        fontSize: '14px', fontWeight: '700',
        cursor: exporting ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s',
        display: 'flex', alignItems: 'center', gap: '8px',
        opacity: exporting ? 0.6 : 1,
      }}
    >
      📄 {exporting ? t('pdf.overlay.generating') : t('pdf.overlay.button')}
    </button>
    {exportError && (
      <div role="alert" style={{ marginTop: '4px', fontSize: '12px', color: darkMode ? '#F87171' : '#BF311A' }}>
        {exportError}
      </div>
    )}
    {exportWarnings.length > 0 && (
      <div role="status" style={{ marginTop: '4px', fontSize: '12px', color: darkMode ? '#FBBF24' : '#B45309' }}>
        {exportWarnings.map((warning) => <div key={warning}>{warning}</div>)}
      </div>
    )}
    </div>
    </>
  );
};

export default PDFExport;
